/**
 * useExportVendeurExcel.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Hook d'export Excel pour VendeurView – Nawara BI
 *
 * Structure du classeur généré :
 *  • Feuille "Tableau"     : données brutes du mode Tableau avec totaux
 *  • Feuille "Résumé"      : infos période, mesures, paramètres
 *
 * Dépendances : exceljs  (npm install exceljs)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback } from "react";
import ExcelJS from "exceljs";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface VendeurExcelRow {
  couleur: string; // nom du vendeur (champ réutilisé depuis CouleurExcelRow)
  values: number[]; // valeurs des mesures dans l'ordre de `headers`
  coutTTC?: number | null;
  marge?: number | null;
}

export interface VendeurExportExcelOptions {
  filename?: string;
  dateFrom?: string;
  dateTo?: string;
  topN?: number | null;
  headers: string[]; // noms des mesures (sans "Vendeur")
  rows: VendeurExcelRow[];
  grandTotals: number[];
  totalCoutTTC?: number | null;
  totalMarge?: number | null;
  hasCoutTTC?: boolean;
  hasMarge?: boolean;
  selectedMagasin?: string | null;
  soldeCode?: string | null;
  demarquePhase?: string | null;
  viewMode: string;
  // Champs spécifiques Vendeur
  dimensionLabel?: string; // ex: "Dépôt", "Vendeur"
  dimensionName?: string;  // ex: "Vendeur"
  title?: string;          // ex: "Analyse Vendeur — Détail DUPONT"
  reportTitle?: string;    // ex: "Analyse Vendeur – Détail DUPONT"
  reportLabel?: string;    // ex: "Analyse Vendeur"
  isDrilldown?: boolean;
  drilldownLabel?: string;
}

// ── Filtre valeurs indésirables ──────────────────────────────────────────────
const INVALID_DIM_RE = /^\s*(all|unknown|couleur inconnue|inconnue|non renseign[ée]|n\/a|na|null|undefined|#n\/a)\s*$/i;
/** Retourne false si la valeur est vide, "all", "Unknown", "inconnue", etc. */
function isValidDimension(v: string | null | undefined): boolean {
  if (!v || v.trim() === "") return false;
  return !INVALID_DIM_RE.test(v.trim());
}

// ── Palette ────────────────────────────────────────────────────────────────────

const VIOLET = "6B21A8";
const VIOLET_DARK = "4C1D95";
const VIOLET_LIGHT = "F5F3FF";
const ROSE = "EC4899";
const TEAL = "0D9488";
const DANGER = "DC2626";
const SUCCESS = "16A34A";
const GRAY50 = "F9FAFB";
const GRAY100 = "F3F4F6";
const GRAY200 = "E5E7EB";
const GRAY700 = "374151";

function hex(color: string): ExcelJS.Color {
  const c = color.replace("#", "").toUpperCase();
  return { argb: `FF${c}` } as ExcelJS.Color;
}

function lighten(color: string, amount: number): ExcelJS.Color {
  const c = color.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const blend = (v: number) =>
    Math.round(v + (255 - v) * amount)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return { argb: `FF${blend(r)}${blend(g)}${blend(b)}` } as ExcelJS.Color;
}

// ── Feuille principale Tableau ────────────────────────────────────────────────

function buildTableauSheet(
  wb: ExcelJS.Workbook,
  opts: VendeurExportExcelOptions,
) {
  const rawRows = (opts.rows ?? []).filter(r => isValidDimension(r.couleur));
  const rows = rawRows;
  const {
    headers,
    grandTotals,
    totalCoutTTC,
    totalMarge,
    hasCoutTTC,
    hasMarge,
    dateFrom,
    dateTo,
    topN,
    selectedMagasin,
    soldeCode,
    demarquePhase,
  } = opts;

  const ws = wb.addWorksheet("Tableau", {
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1 },
  });

  // ── Largeurs colonnes : 1 col Vendeur + N mesures + optionnels ──
  const dimColLabel = opts.dimensionLabel ?? "Vendeur";
  const allCols = [dimColLabel, ...headers];
  if (hasCoutTTC) allCols.push("Coût TTC");
  if (hasMarge) allCols.push("Marge");

  ws.columns = allCols.map((h, i) => ({
    key: `col${i}`,
    width: i === 0 ? 30 : 18,
  }));

  // ── Ligne 1 : Titre ──
  ws.mergeCells(`A1:${String.fromCharCode(64 + allCols.length)}1`);
  const titleCell = ws.getCell("A1");
  const pageTitle =
    opts.isDrilldown && opts.drilldownLabel
      ? `👤  Détail par Dépôt — ${opts.drilldownLabel} – Avantages Soft`
      : opts.title
      ? `👤  ${opts.title} – Avantages Soft`
      : opts.reportTitle
      ? `👤  ${opts.reportTitle} – Avantages Soft`
      : `👤  Analyse Vendeur – Avantages Soft`;
  titleCell.value = pageTitle;
  titleCell.font = {
    name: "Calibri",
    bold: true,
    size: 16,
    color: hex(VIOLET),
  };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: hex(VIOLET_LIGHT),
  };
  ws.getRow(1).height = 36;

  // ── Ligne 2 : Paramètres ──
  ws.mergeCells(`A2:${String.fromCharCode(64 + allCols.length)}2`);
  const paramParts: string[] = [];
  if (opts.isDrilldown && opts.drilldownLabel)
    paramParts.push(`Vendeur : ${opts.drilldownLabel}`);
  if (soldeCode) paramParts.push(`Solde : ${soldeCode}`);
  if (demarquePhase) paramParts.push(`Démarque : ${demarquePhase}`);
  if (dateFrom && dateTo)
    paramParts.push(`Période : ${dateFrom} → ${dateTo}`);
  else if (dateFrom) paramParts.push(`À partir du : ${dateFrom}`);
  if (selectedMagasin) paramParts.push(`Magasin : ${selectedMagasin}`);
  if (topN) paramParts.push(`Top ${topN}`);
  paramParts.push(`Généré le : ${new Date().toLocaleString("fr-FR")}`);
  const paramCell = ws.getCell("A2");
  paramCell.value = paramParts.join("   |   ");
  paramCell.font = {
    name: "Calibri",
    italic: true,
    size: 10,
    color: hex("9B4DB5"),
  };
  paramCell.alignment = { horizontal: "center" };
  paramCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: hex("F9F5FF"),
  };
  ws.getRow(2).height = 18;

  // ── Ligne 3 vide ──
  ws.getRow(3).height = 6;

  // ── Ligne 4 : En-têtes colonnes ──
  const hRow = ws.getRow(4);
  allCols.forEach((h, i) => {
    const cell = hRow.getCell(i + 1);
    cell.value = h;
    cell.font = {
      name: "Calibri",
      bold: true,
      size: 10,
      color: { argb: "FFFFFFFF" },
    };
    const bg =
      h === "Coût TTC"
        ? TEAL
        : h === "Marge"
        ? SUCCESS
        : i === 0
        ? VIOLET_DARK
        : VIOLET;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: hex(bg) };
    cell.alignment = {
      horizontal: i === 0 ? "left" : "right",
      vertical: "middle",
    };
    cell.border = {
      bottom: { style: "medium", color: { argb: "FFFFFFFF" } },
      right: { style: "thin", color: lighten(VIOLET, 0.3) },
    };
  });
  hRow.height = 22;

  // ── Lignes données ──
  rows.forEach((row, idx) => {
    const dRow = ws.getRow(5 + idx);

    // Vendeur (dimension)
    const labelCell = dRow.getCell(1);
    labelCell.value = row.couleur; // "couleur" = nom du vendeur dans VendeurView
    labelCell.font = { name: "Calibri", size: 10, bold: false };
    labelCell.alignment = { horizontal: "left", vertical: "middle" };

    // Valeurs mesures
    row.values.forEach((val, vi) => {
      const cell = dRow.getCell(2 + vi);
      cell.value = typeof val === "number" && !isNaN(val) ? val : 0;
      cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "right" };
      cell.font = { name: "Calibri", size: 10 };
    });

    // Coût TTC
    if (hasCoutTTC && row.coutTTC != null) {
      const c = dRow.getCell(1 + headers.length + 1);
      c.value = row.coutTTC;
      c.numFmt = "#,##0.00";
      c.alignment = { horizontal: "right" };
      c.font = { name: "Calibri", size: 10, color: hex(TEAL) };
    }

    // Marge
    if (hasMarge && row.marge != null) {
      const c = dRow.getCell(1 + headers.length + (hasCoutTTC ? 2 : 1));
      c.value = row.marge;
      c.numFmt = "#,##0.00";
      c.alignment = { horizontal: "right" };
      c.font = {
        name: "Calibri",
        size: 10,
        color: row.marge >= 0 ? hex(SUCCESS) : hex(DANGER),
        bold: false,
      };
    }

    // Fond alterné
    if (idx % 2 === 1) {
      for (let c = 1; c <= allCols.length; c++) {
        dRow.getCell(c).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: hex(GRAY50),
        };
      }
    }

    dRow.height = 17;
  });

  // ── Ligne Total Général ──
  if (rows.length > 1) {
    const totalRowIdx = 5 + rows.length;
    const totRow = ws.getRow(totalRowIdx);

    const labelCell = totRow.getCell(1);
    const dimLabel = opts.dimensionLabel ?? "vendeurs";
    labelCell.value = `∑ TOTAL GÉNÉRAL  (${rows.length} ${dimLabel.toLowerCase()})`;
    labelCell.font = {
      name: "Calibri",
      bold: true,
      size: 11,
      color: { argb: "FFFFFFFF" },
    };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: hex(VIOLET_DARK),
    };
    labelCell.alignment = { horizontal: "left" };

    grandTotals.forEach((val, i) => {
      const cell = totRow.getCell(2 + i);
      cell.value = val;
      cell.numFmt = "#,##0.00";
      cell.font = {
        name: "Calibri",
        bold: true,
        size: 11,
        color: hex("FBCFE8"),
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: hex(VIOLET_DARK),
      };
      cell.alignment = { horizontal: "right" };
    });

    if (hasCoutTTC && totalCoutTTC != null) {
      const c = totRow.getCell(1 + headers.length + 1);
      c.value = totalCoutTTC;
      c.numFmt = "#,##0.00";
      c.font = {
        name: "Calibri",
        bold: true,
        size: 11,
        color: { argb: "FFFFFFFF" },
      };
      c.fill = { type: "pattern", pattern: "solid", fgColor: hex(TEAL) };
      c.alignment = { horizontal: "right" };
    }

    if (hasMarge && totalMarge != null) {
      const c = totRow.getCell(1 + headers.length + (hasCoutTTC ? 2 : 1));
      c.value = totalMarge;
      c.numFmt = "#,##0.00";
      c.font = {
        name: "Calibri",
        bold: true,
        size: 11,
        color: { argb: "FFFFFFFF" },
      };
      c.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: hex(totalMarge >= 0 ? SUCCESS : DANGER),
      };
      c.alignment = { horizontal: "right" };
    }

    totRow.height = 22;
  }

  // ── Figer ligne d'en-tête ──
  ws.views = [{ state: "frozen", xSplit: 1, ySplit: 4 }];

  // ── Filtre auto ──
  ws.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4 + rows.length, column: allCols.length },
  };
}

// ── Feuille Résumé ────────────────────────────────────────────────────────────

function buildResumeSheet(
  wb: ExcelJS.Workbook,
  opts: VendeurExportExcelOptions,
) {
  const ws = wb.addWorksheet("Résumé");

  ws.columns = [
    { key: "label", width: 24 },
    { key: "value", width: 40 },
  ];

  const addRow = (label: string, value: string | number, bold = false) => {
    const r = ws.addRow([label, value]);
    r.getCell(1).font = {
      name: "Calibri",
      bold: true,
      size: 10,
      color: hex(VIOLET),
    };
    r.getCell(2).font = { name: "Calibri", size: 10, bold };
    r.height = 17;
  };

  // Titre
  ws.mergeCells("A1:B1");
  const t = ws.getCell("A1");
  const resumeTitle = opts.reportLabel
    ? `👤  Paramètres de l'export — ${opts.reportLabel}`
    : `👤  Paramètres de l'export — Analyse Vendeur`;
  t.value = resumeTitle;
  t.font = { name: "Calibri", bold: true, size: 14, color: hex(VIOLET) };
  t.fill = { type: "pattern", pattern: "solid", fgColor: hex(VIOLET_LIGHT) };
  t.alignment = { horizontal: "center" };
  ws.getRow(1).height = 30;
  ws.addRow([]);

  addRow("Date d'export", new Date().toLocaleString("fr-FR"));
  addRow(
    "Vue active",
    opts.viewMode.charAt(0).toUpperCase() + opts.viewMode.slice(1),
  );
  addRow("Mesures sélectionnées", opts.headers.join(", "));
  if (opts.dateFrom) addRow("Date début", opts.dateFrom);
  if (opts.dateTo) addRow("Date fin", opts.dateTo);
  if (opts.soldeCode) addRow("Solde sélectionné", opts.soldeCode);
  if (opts.demarquePhase) addRow("Phase démarque", opts.demarquePhase);
  if (opts.selectedMagasin) addRow("Magasin / Dépôt", opts.selectedMagasin);
  if (opts.topN) addRow("Top N", opts.topN);
  const dimLabel = opts.dimensionLabel ?? "vendeurs";
  addRow(`Nombre de ${dimLabel.toLowerCase()}`, opts.rows.length, true);
}

// ── Hook principal ─────────────────────────────────────────────────────────────

export function useExportVendeurExcel() {
  const exportExcel = useCallback(async (opts: VendeurExportExcelOptions) => {
    const filename =
      opts.filename ??
      `Vendeur_Export_${new Date().toISOString().slice(0, 10)}`;

    const wb = new ExcelJS.Workbook();
    wb.creator = "Avantages Soft";
    wb.created = new Date();
    wb.modified = new Date();
    wb.title = `Analyse Vendeur – ${filename}`;
    wb.subject = "Export Vendeur Avantages Soft";
    wb.keywords = "Avantages,Soft,vendeur,export";

    buildTableauSheet(wb, opts);
    buildResumeSheet(wb, opts);

    // Téléchargement
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return { exportExcel };
}